import type { Clip } from "@hudhud/core";

export const FPS = 30;
export const WIDTH = 1080;
export const HEIGHT = 1920;
export const REEL_ID = "Reel";

export interface ReelProps {
  clip: Clip;
  /** URL of the per-clip media file (trimmed source) */
  mediaUrl: string;
  /** source ms at t=0 of the media file */
  mediaStartMs: number;
  /** the logo over the video (and the cover): the clip's own, or the studio's pick for the footage's brightness */
  logoUrl: string;
  /** the marks the opener and the end card choose from, by their own background: a light one on a dark card, a dark one on a light card */
  logoSet?: { onDark?: string; onLight?: string };
  /** base URL of the self-hosted fonts folder (assets/fonts) */
  fontsBaseUrl: string;
  musicUrl?: string;
  /** the whoosh (kept for older props); prefer sfxUrls */
  sfxUrl?: string;
  /** transparent (VP9 alpha) video of the speaker alone, cut exactly like mediaUrl; when given, behind-the-speaker texts go behind them */
  cutoutUrl?: string;
  /** folder url holding the clip's reference images (clips/<id>/overlays) */
  overlaysBaseUrl?: string;
  /** sound library: id -> url (assets/sfx/manifest.json) */
  sfxUrls?: Record<string, string>;
  /** preview-only: show the opener fully composed (no entry animation) — the editor passes this while paused so the first frame is the finished slide */
  settleOpener?: boolean;
  /** preview-only: hear the full mix, only the music, or only the voice */
  audition?: "mix" | "music" | "voice";
  /** preview-only: skip the colour grade and the heavy effect layers so a phone keeps up; the render never sets this */
  lite?: boolean;
  /** preview-only: drawn by the studio's live player, where video frames come from the browser's own decoder (the renderer's ffmpeg bridge is not there) */
  player?: boolean;
  /** preview-only: mix through the Web Audio API, because iOS Safari ignores a media element's volume (the music would drown the voice) */
  webAudio?: boolean;
  /** render the cover image instead of the reel: the chosen still with the hook title and logo, no captions */
  cover?: boolean;
}

export const COLORS = {
  green: "#1B4D2E",
  logoGreen: "#22B14C",
  gold: "#F5C542",
  offWhite: "#F7F5EF",
  ink: "#0B0F0D",
};

export const msToFrame = (ms: number) => Math.round((ms / 1000) * FPS);
export const frameToMs = (frame: number) => (frame / FPS) * 1000;

/**
 * House mix for the two sounds a clip plays. whoosh-soft measures −11.3 LUFS; at 0.16 (−16 dB) it lands at about −27 LUFS,
 * just under the speech, which sits near −26 LUFS with the voice at the house −6 dB. (Was 0.5 / 0.4: 9 and 5 LU above the voice.)
 */
export const OPENER_SFX_GAIN = 0.16;
export const END_CARD_SFX_GAIN = 0.16;

/** The logo for a card: the one made for its background when the set has it, else the clip's logo. */
export const logoFor = (set: ReelProps["logoSet"] | undefined, light: boolean, fallback: string): string => (light ? set?.onLight : set?.onDark) ?? fallback;
